import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listRegisterLogServer, RegisterLogEntry } from '../services/MendsVisionService';
import { listRegisterLog } from '../services/registerLog';
import { outcomeLabel } from '../lib/registerOutcome';
import { RegisterActionControls } from './RegisterActionControls';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Histórico do Registrar lido do servidor (REGX-05 / REG-40): o mesmo relato aparece para qualquer usuário e
 * dispositivo do setor, com autor, origem, itens e o estado atual de cada aprovação ligada. O localStorage só entra
 * como reserva quando o servidor não responde, e a tela diz isso.
 */
export interface RegisterHistoryListProps {
  sectorId: number;
  contactId?: number | null;
  /** Filtro de estado (ex.: só os que aguardam identidade). */
  status?: string;
  limit?: number;
  compact?: boolean;
  /** Muda para recarregar (ex.: depois de um novo relato). */
  refreshKey?: number;
  emptyText?: string;
  onCount?: (n: number) => void;
}

const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Processado',
  awaiting_clarification: 'Aguardando esclarecimento',
  draft: 'Interpretado, não executado',
  failed: 'Falhou',
};

const INPUT_LABEL: Record<string, string> = {
  voice: 'voz',
  text: 'texto',
  file: 'arquivo',
  mixed: 'voz/texto + arquivo',
};

const SURFACE_LABEL: Record<string, string> = {
  global: 'menu global',
  cliente: 'ficha do cliente',
  inbox: 'Inbox',
  oportunidade: 'oportunidade',
  funil: 'funil',
  hoje: 'Hoje',
  central: 'Central',
  register: 'Registrar',
};

function statusTone(status: string) {
  if (status === 'confirmed') return 'mai-pill mai-pill-ai';
  if (status === 'awaiting_clarification') return 'mai-pill mai-pill-wait';
  if (status === 'failed') return 'mai-pill mai-pill-high';
  return 'mai-pill';
}

function when(iso: string) {
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/** Relatos só deste navegador (reserva sem servidor), no mesmo formato da lista. */
function localFallback(sectorId: number, contactId?: number | null): RegisterLogEntry[] {
  return listRegisterLog(sectorId)
    .filter((e) => !contactId || e.contactId === contactId)
    .map((e, idx) => ({
      id: -(idx + 1),
      sectorId,
      requestId: e.id,
      status: e.status,
      inputType: 'text',
      rawTranscript: e.rawTranscript,
      summary: e.summary,
      contactId: e.contactId,
      contactName: e.contactName,
      sourceSurface: e.sourceSurface,
      attachments: [],
      items: [],
      approvalIds: e.approvalIds || [],
      memorySaved: !!e.memorySaved,
      errorMessage: e.errorMessage,
      candidates: e.candidates,
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
    }));
}

export const RegisterHistoryList: React.FC<RegisterHistoryListProps> = ({
  sectorId,
  contactId,
  status,
  limit = 40,
  compact,
  refreshKey,
  emptyText,
  onCount,
}) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<RegisterLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [offline, setOffline] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!sectorId) return;
    setLoading(true);
    try {
      const data = await listRegisterLogServer(sectorId, { contactId, limit });
      setEntries(data);
      setOffline(null);
    } catch (e: unknown) {
      setEntries(localFallback(sectorId, contactId));
      setOffline(apiErrorMessage(e, 'Servidor indisponível'));
    } finally {
      setLoading(false);
    }
  }, [sectorId, contactId, limit]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const shown = status ? entries.filter((e) => e.status === status) : entries;
  useEffect(() => {
    onCount?.(shown.length);
  }, [shown.length, onCount]);

  if (loading && entries.length === 0) {
    return <p className="mai-card-detail" aria-busy="true">Carregando relatos…</p>;
  }

  return (
    <div className={`mai-reg-history ${compact ? 'is-compact' : ''}`}>
      {offline && (
        <p className="mai-form-error" role="status">
          Histórico do servidor indisponível ({offline}). Mostrando só o que ficou neste navegador.
        </p>
      )}
      {shown.length === 0 && (
        <p className="mai-empty">{emptyText || 'Nenhum relato registrado ainda.'}</p>
      )}
      {shown.map((entry) => (
        <div key={`${entry.id}-${entry.requestId}`} className="mai-card mai-reg-log-card">
          <div className="mai-card-row">
            <div style={{ minWidth: 0 }}>
              <span className={statusTone(entry.status)}>{STATUS_LABEL[entry.status] || entry.status}</span>
              <p className="mai-card-title" style={{ marginTop: 8 }}>
                {entry.summary || entry.rawTranscript.slice(0, 120) || 'Relato'}
              </p>
              <p className="mai-card-detail">
                {entry.authorName ? `${entry.authorName} · ` : ''}
                {when(entry.createdAt)}
                {!compact && entry.contactName ? ` · ${entry.contactName}` : ''}
                {` · ${INPUT_LABEL[entry.inputType] || entry.inputType}`}
                {entry.sourceSurface ? ` · ${SURFACE_LABEL[entry.sourceSurface] || entry.sourceSurface}` : ''}
              </p>
              {entry.items.length > 0 && (
                <ul className="mai-reg-action-list" style={{ marginTop: 8 }}>
                  {entry.items.map((item, i) => {
                    const meta = outcomeLabel(item.currentOutcome || item.outcome, item.actionKind, item.reasonCode);
                    return (
                      <li key={`${item.actionId}-${i}`}>
                        <div className="mai-reg-action-row">
                          <strong>{item.title || item.actionKind}</strong>
                          <span className={meta.tone}>{meta.label}</span>
                        </div>
                        {(item.currentResult || item.result) && !compact && (
                          <span className="mai-card-detail">{item.currentResult || item.result}</span>
                        )}
                        {item.evidence && !compact && (
                          <span className="mai-card-detail">Evidência: “{item.evidence}”</span>
                        )}
                        {!compact && entry.id > 0 && (
                          <RegisterActionControls
                            sectorId={sectorId}
                            requestId={entry.requestId}
                            item={{ ...item, outcome: item.currentOutcome || item.outcome }}
                            onUpdate={() => void load()}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {entry.attachments.length > 0 && (
                <p className="mai-card-detail">
                  Anexos: {entry.attachments.map((a) => (a.readable ? a.fileName : `${a.fileName} (não lido)`)).join(', ')}
                </p>
              )}
              {entry.status === 'awaiting_clarification' && (entry.candidates || []).length > 0 && (
                <p className="mai-card-detail">
                  Candidatos: {(entry.candidates || []).map((c) => c.name).join(', ')}
                </p>
              )}
              {entry.errorMessage && <p className="mai-form-error" style={{ marginTop: 6 }}>{entry.errorMessage}</p>}
            </div>
            {!compact && (
              <div className="mai-btn-row" style={{ flexDirection: 'column', gap: 6 }}>
                {entry.contactId ? (
                  <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate(`/chat?contactId=${entry.contactId}`)}>
                    Conversa
                  </button>
                ) : null}
                {entry.approvalIds[0] ? (
                  <button
                    type="button"
                    className="mai-btn mai-btn-soft"
                    onClick={() => navigate(`/central-inteligencia?tab=aprovacoes&id=${entry.approvalIds[0]}`)}
                  >
                    Abrir na Central
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
